import { useState } from 'react';
import { 
  Phone, 
  Mail, 
  MapPin, 
  Clock, 
  MessageSquare, 
  Send, 
  CheckCircle,
  Users,
  Globe,
  Building
} from 'lucide-react';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const contactMethods = [
    {
      icon: <Phone className="h-5 w-5" />,
      title: 'Phone Support',
      details: '+91-9100390049',
      subtitle: 'Available 24/7',
      color: 'bg-blue-100 text-blue-600'
    },
    {
      icon: <Mail className="h-5 w-5" />,
      title: 'Email Us',
      details: 'shreepujatraveldesk@gmail.com',
      subtitle: 'Response within 24 hours',
      color: 'bg-green-100 text-green-600'
    },
    {
      icon: <MessageSquare className="h-5 w-5" />,
      title: 'Live Chat',
      details: 'Chat Now',
      subtitle: 'Instant support',
      color: 'bg-purple-100 text-purple-600'
    },
    {
      icon: <MapPin className="h-5 w-5" />,
      title: 'Visit Our Office',
      details: 'Hyderabad, Telangana, India',
      subtitle: 'Mon-Fri, 9AM-6PM',
      color: 'bg-orange-100 text-orange-600'
    }
  ];

  const faqs = [
    {
      question: 'How do I cancel a ride?',
      answer: 'You can cancel rides from the My Rides section up to 30 minutes before pickup.'
    },
    {
      question: 'What payment methods do you accept?',
      answer: 'We accept all major credit cards, PayPal, and digital wallets like Apple Pay and Google Pay.'
    },
    {
      question: 'How do I become a driver?',
      answer: 'Visit our careers page and submit your application. We\'ll guide you through the process.'
    },
    {
      question: 'Do you offer corporate accounts?',
      answer: 'Yes! Contact our business team for custom corporate solutions.'
    }
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    setIsSubmitting(false);
    setIsSubmitted(true);
    
    // Reset form after 3 seconds
    setTimeout(() => {
      setIsSubmitted(false);
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });
    }, 3000);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 pt-16 pb-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto">
        {/* Header Section */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-3">
            Get in <span className="text-cyan-600" style={{ color: 'rgba(65, 1, 150, 1)' }}>Touch</span>
          </h1>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Have questions? We're here to help. Reach out to our team through any of the channels below.
          </p>
        </div>

        {/* Contact Methods Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
          {contactMethods.map((method, index) => (
            <div 
              key={index}
              className="bg-white rounded-xl p-5 shadow-md hover:shadow-lg transition-shadow duration-300 border border-gray-100"
            >
              <div className={`inline-flex p-2.5 rounded-lg ${method.color} mb-3`}>
                {method.icon}
              </div>
              <h3 className="font-semibold text-gray-900 mb-1.5">
                {method.title}
              </h3>
              <p className="text-gray-900 font-medium mb-1">
                {method.details}
              </p>
              <p className="text-sm text-gray-500">
                {method.subtitle}
              </p>
            </div>
          ))}
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Contact Form */}
          <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
            <div className="flex items-center mb-6">
              <div className="p-2.5 bg-cyan-100 rounded-lg mr-3" >
                <Send className="h-5 w-5 text-cyan-600"style={{ color: 'rgba(65, 1, 150, 1)' }} />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">
                  Send us a Message
                </h2>
                <p className="text-gray-600">
                  We'll get back to you as soon as possible
                </p>
              </div>
            </div>

            {isSubmitted ? (
              <div className="text-center py-8">
                <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-3" />
                <h3 className="text-lg font-semibold text-gray-900 mb-1.5">
                  Message Sent Successfully!
                </h3>
                <p className="text-gray-600">
                  Thank you for contacting us. We'll get back to you within 24 hours.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Your Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-3 py-2.5 rounded-lg border border-gray-300 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-200 transition-colors"
                      placeholder="Your full name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-3 py-2.5 rounded-lg border border-gray-300 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-200 transition-colors"
                      placeholder="Yourmail@example.com"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-3 py-2.5 rounded-lg border border-gray-300 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-200 transition-colors"
                    placeholder="+91-0123456789"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Subject *
                  </label>
                  <input
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2.5 rounded-lg border border-gray-300 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-200 transition-colors"
                    placeholder="How can we help you?"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Message *
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={4}
                    className="w-full px-3 py-2.5 rounded-lg border border-gray-300 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-200 transition-colors resize-none"
                    placeholder="Please provide details about your inquiry..."
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`w-full py-3 px-6 rounded-lg font-semibold text-white transition-all duration-300 ${
                    isSubmitting 
                      ? 'bg-yellow-600 cursor-not-allowed' 
                      : 'bg-yellow-400 hover:bg-yellow-700 hover:shadow-md'
                  }`}
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin h-4 w-4 mr-2 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Sending...
                    </span>
                  ) : (
                    'Send Message'
                  )}
                </button>
              </form>
            )}
          </div>

          {/* Right Side - Info & FAQ */}
          <div className="space-y-6">
            {/* Company Info */}
            <div className="bg-gradient-to-br from-cyan-600 to-blue-600 rounded-xl p-6 text-white"style={{ background: 'rgba(65, 1, 150, 1)' }}>
              <h3 className="text-xl font-bold mb-4">Our Commitment</h3>
              <div className="space-y-3">
                <div className="flex items-start">
                  <Clock className="h-4 w-4 mt-0.5 mr-2.5" />
                  <div>
                    <h4 className="font-semibold">24/7 Support</h4>
                    <p className="text-cyan-100 text-sm">Round-the-clock assistance for all your needs</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <Users className="h-4 w-4 mt-0.5 mr-2.5" />
                  <div>
                    <h4 className="font-semibold">Expert Team</h4>
                    <p className="text-cyan-100 text-sm">Professional support staff ready to help</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <Globe className="h-4 w-4 mt-0.5 mr-2.5" />
                  <div>
                    <h4 className="font-semibold">Global Coverage</h4>
                    <p className="text-cyan-100 text-sm">Serving customers in 50+ countries</p>
                  </div>
                </div>
              </div>
            </div>

            {/* FAQ Section */}
            <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
              <div className="flex items-center mb-4">
                <div className="p-1.5 bg-blue-100 rounded-md mr-2.5">
                  <Building className="h-4 w-4 text-blue-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-900">
                  Frequently Asked Questions
                </h3>
              </div>
              <div className="space-y-4">
                {faqs.map((faq, index) => (
                  <div key={index} className="pb-4 border-b border-gray-100 last:border-0 last:pb-0">
                    <h4 className="font-semibold text-gray-900 mb-1.5">
                      {faq.question}
                    </h4>
                    <p className="text-gray-600 text-sm">
                      {faq.answer}
                    </p>
                  </div>
                ))}
              </div>
              <div className="mt-6 pt-4 border-t border-gray-100">
                <p className="text-gray-600 text-sm">
                  Can't find your answer?{' '}
                  <a href="/help" className="text-cyan-600 font-semibold hover:text-cyan-700"style={{ color: 'rgba(65, 1, 150, 1)' }}>
                    Visit our Help Center →
                  </a>
                </p>
              </div>
            </div>

            {/* Business Hours */}
            <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
              <h4 className="font-semibold text-gray-900 mb-3">Business Hours</h4>
              <div className="space-y-2.5">
                <div className="flex justify-between">
                  <span className="text-gray-600">Monday - Friday</span>
                  <span className="font-medium">9:00 AM - 6:00 PM</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Saturday</span>
                  <span className="font-medium">10:00 AM - 4:00 PM</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Sunday</span>
                  <span className="font-medium">Emergency Support Only</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Map Section */}
       <div className="mt-12 bg-white rounded-xl shadow-lg p-6 border border-gray-100">
  <h3 className="text-xl font-bold text-gray-900 mb-4">Find Us Here</h3>
  <div className="h-72 rounded-lg overflow-hidden">
    <iframe 
      src="https://www.google.com/maps/embed?pb=!1m16!1m12!1m3!1d60896.8306327678!2d78.37174704718154!3d17.45722916092053!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!2m1!1skukatpally%20hyderabad%20pin%20code!5e0!3m2!1sen!2sin!4v1767428743168!5m2!1sen!2sin" 
      width="100%" 
      height="100%" 
      style={{ border: 0 }}
      allowFullScreen 
      loading="lazy" 
      referrerPolicy="no-referrer-when-downgrade"
      title="Shree Puja Travels Location - Kukatpally, Hyderabad"
      aria-label="Google Maps showing Shree Puja Travels location in Kukatpally, Hyderabad"
    />
  </div>
  <div className="text-center mt-4">
    <a 
      href="https://maps.google.com/?q=Kukatpally,Hyderabad"
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center px-5 py-2.5 bg-cyan-600 text-white rounded-lg font-medium hover:bg-cyan-700 transition-colors"style={{ background: 'rgba(65, 1, 150, 1)' }}
    >
      <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
        <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
      </svg>
      Get Directions
    </a>
  </div>
</div>
      </div>
    </div>
  );
};

export default ContactPage;